import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  createDataTableColumnHelper,
  DataTable,
  dataTableFeatures,
  MultiOrganizationSelect,
  Spacer,
  Tooltip,
} from '@porsche-code/kado-ui';
import {
  PButton,
  PButtonPure,
  PHeading,
  PTag,
} from '@porsche-design-system/components-react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ColumnFiltersState, useTable } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ExportModal } from '@/components';
import { App } from '@/config/app';
import { NAMESPACES } from '@/config/i18n';
import { appFor, Role } from '@/config/role';
import {
  useOrganizationInfoItemsQuery,
  useOrganizationTreeQuery,
} from '@/graphql';
import { useApplicationConfigs } from '@/hooks';

type FlattenedUserRole = {
  id: string;
  ppnuid: string;
  name: string;
  organizationId: number;
  organizationName: string;
  role: string;
  app: string;
  personId: number;
};

const columnHelper = createDataTableColumnHelper<FlattenedUserRole>();

export const Route = createFileRoute('/admin/organization-info/_auth/')({
  component: OrganizationInfo,
  beforeLoad: () => {
    return {
      breadcrumb: () => <Trans i18nKey="admin.organizationInfo.headline" />,
    };
  },
});

function OrganizationInfo() {
  const { t, i18n } = useTranslation(NAMESPACES);
  const appConfigs = useApplicationConfigs();
  const [selectedOrganizations, setSelectedOrganizations] = useState<number[]>(
    [],
  );
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const { data: organizationsData, isPending: isLoadingOrganizations } =
    useOrganizationTreeQuery(undefined, {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  const organizationTree = useMemo(
    () => organizationsData?.organizations.docs || [],
    [organizationsData],
  );

  const { data: persons, isPending } = useOrganizationInfoItemsQuery(
    {
      filter: {
        deletedAt: { _exists: false },
        roles: { _in: Object.values(Role) },
        organization_organizationId: {
          _in: selectedOrganizations,
        },
      },
      limit: 0,
    },
    {
      enabled: selectedOrganizations.length > 0,
      select: (data) => data.persons.docs,
    },
  );

  const flattenedData = useMemo(() => {
    if (!persons || persons.length === 0) return [];

    const result: FlattenedUserRole[] = [];

    persons.forEach((person) => {
      if (!person.organization) return;

      const kadoRoles = person.roles.filter(
        (role) => role === Role.SystemAdmin || appFor(role),
      );
      if (kadoRoles.length === 0) return;

      kadoRoles.forEach((role) => {
        const apps =
          role === Role.SystemAdmin
            ? Object.values(App)
            : [appFor(role)].filter(Boolean);

        apps.forEach((app) => {
          if (!app) return;
          result.push({
            id: `person-${person.id}-${app}-${role}`,
            ppnuid: person.ppnuid,
            name: person.name,
            organizationId: person.organization!.id,
            organizationName: person.organization!.name,
            role: role,
            app: app,
            personId: person.id,
          });
        });
      });
    });

    return result;
  }, [persons]);

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('ppnuid', {
          cell: (data) => data.getValue(),
          header: t('common:ppnuid'),
          meta: {
            name: t('common:ppnuid'),
          },
          filterFn: 'includesString',
        }),
        columnHelper.accessor('name', {
          cell: (data) => data.getValue(),
          header: t('common:name'),
          meta: {
            name: t('common:name'),
          },
          filterFn: 'includesString',
        }),
        columnHelper.accessor('organizationName', {
          cell: (data) => data.getValue(),
          header: t('common:organization'),
          meta: {
            name: t('common:organization'),
          },
          filterFn: 'includesString',
        }),
        columnHelper.accessor('role', {
          cell: (data) => {
            return data.getValue() as string;
          },
          header: t('admin.organizationInfo.role'),
          meta: {
            name: t('admin.organizationInfo.role'),
          },
          filterFn: 'includesString',
        }),
        columnHelper.accessor('app', {
          cell: (data) => {
            const app = data.getValue() as App;
            return <PTag>{appConfigs[app]?.name || app}</PTag>;
          },
          header: t('common:application'),
          meta: {
            name: t('common:application'),
            filterType: 'select',
            filterOptions: Object.values(App).map((app) => ({
              label: appConfigs[app as App].name,
              value: app,
            })),
          },
          filterFn: 'equalsString',
        }),
        columnHelper.display({
          id: 'actions',
          header: () => (
            <TableHeaderActions>{t('common:actions')}</TableHeaderActions>
          ),
          size: 50,
          cell: (data) => {
            const personId = data.row.original.personId;
            return (
              <TableCellActions>
                <Link
                  to="/admin/person-info/$personId/account"
                  params={{ personId: String(personId) }}
                  target="_blank"
                >
                  <Tooltip content={t('admin.organizationInfo.goToPersonInfo')}>
                    <PButtonPure type="button" icon="external" hideLabel>
                      {t('admin.organizationInfo.goToPersonInfo')}
                    </PButtonPure>
                  </Tooltip>
                </Link>
              </TableCellActions>
            );
          },
          enableColumnFilter: false,
          enableSorting: false,
        }),
      ]),
    [t, appConfigs],
  );

  const exportFilename = useMemo(() => {
    const headline = t('admin.organizationInfo.headline');

    if (selectedOrganizations.length === 1) {
      const org = organizationTree.find(
        (o) => o.id === selectedOrganizations[0],
      );
      return `${headline} - ${org?.name || ''}`;
    }
    return headline;
  }, [selectedOrganizations, organizationTree, t]);

  const exportTable = useTable({
    features: dataTableFeatures,
    data: flattenedData,
    columns,
    state: { columnFilters },
  });

  const exportData = useMemo(() => {
    const filteredRows = exportTable.getFilteredRowModel().rows;

    return filteredRows.map((row) => ({
      [t('common:ppnuid')]: row.original.ppnuid,
      [t('common:name')]: row.original.name,
      [t('common:organization')]: row.original.organizationName,
      [t('admin.organizationInfo.role')]: row.original.role,
      [t('common:application')]: appConfigs[row.original.app as App].name,
    }));
  }, [exportTable, t, appConfigs]);

  return (
    <>
      <PHeading size="lg" aria-level={1} tag="h1">
        {t('admin.organizationInfo.headline')}
      </PHeading>
      <Spacer mb="md" />
      <p>{t('admin.organizationInfo.description')}</p>
      <Spacer mb="md" />
      <SelectButtonRow>
        <MultiOrganizationSelect
          label={t('common:organization')}
          placeholder={t('common:selectOrganizations')}
          clearLabel={t('common:action.reset')}
          language={i18n.language}
          isLoading={isLoadingOrganizations}
          organizations={organizationTree}
          xMoreLabel={(count) => t('common:xMore', { count })}
          selectionCountLabel={(count) => t('common:selectionCount', { count })}
          value={selectedOrganizations}
          onChange={setSelectedOrganizations}
        />
        <PButton
          aria={{ 'aria-haspopup': 'dialog' }}
          type="button"
          icon="download"
          disabled={flattenedData.length === 0}
          onClick={() => setExportModalOpen(true)}
        >
          {t('common:action.export')}
        </PButton>
      </SelectButtonRow>
      <Spacer mb="lg" />
      {selectedOrganizations.length > 0 && (
        <DataTable
          isLoading={isPending}
          caption={t('admin.organizationInfo.tableCaption')}
          data={flattenedData}
          columns={columns}
          idAccessor="id"
          onColumnFiltersChange={setColumnFilters}
          pagination={{
            active: true,
            pageSize: 10,
            position: 'bottom',
          }}
          i18n={{
            filterLabel: (columnName) =>
              t('common:iconLabel.filter', { columnName }),
            optionAll: t('common:all'),
            buttonReset: t('common:action.reset'),
            buttonFilter: t('common:action.filter'),
            actionSearch: t('common:action.search'),
            noData: t('common:noData'),
            minLabel: t('common:min'),
            maxLabel: t('common:max'),
          }}
        />
      )}
      {selectedOrganizations.length === 0 && (
        <>
          <Spacer mb="md" />
          <p>{t('admin.organizationInfo.selectOrganization')}</p>
        </>
      )}
      <ExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        data={exportData}
        filename={exportFilename}
      />
    </>
  );
}

const TableCellActions = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'end',
    gap: 'sm',
  },
});

const TableHeaderActions = styled('div', {
  base: {
    textAlign: 'right',
  },
});

const SelectButtonRow = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 'md',
    alignItems: 'end',
  },
});
